/**
 * QR Code Printing System
 * Generate and print QR codes for products, orders, merchants, and packages
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  QrCode,
  Printer,
  Download,
  ArrowLeft,
  RefreshCw,
  Search,
  Package,
  Truck,
  Store,
  MapPin,
  ShoppingCart,
  Plus,
  Trash2,
  Grid,
  List,
  Loader2,
  CheckCircle
} from 'lucide-react';

// QR code types with icons and labels
const QR_TYPES = [
  { id: 'product', label: 'Products', icon: Package, color: 'text-blue-400' },
  { id: 'order', label: 'Orders', icon: ShoppingCart, color: 'text-green-400' },
  { id: 'merchant', label: 'Merchants', icon: Store, color: 'text-purple-400' },
  { id: 'pudo', label: 'PUDO Points', icon: MapPin, color: 'text-amber-400' },
  { id: 'package', label: 'Packages', icon: Package, color: 'text-teal-400' },
  { id: 'driver', label: 'Drivers', icon: Truck, color: 'text-red-400' },
];

const QRCodePrinting = () => {
  const { isAdmin, hasRole } = useAuth();
  const navigate = useNavigate();
  const printRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState('product');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [generatedCodes, setGeneratedCodes] = useState([]);
  const [qrSize, setQrSize] = useState(200);
  const [includeLabel, setIncludeLabel] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [codesPerRow, setCodesPerRow] = useState(3);

  // Check access
  useEffect(() => {
    if (!isAdmin() && !hasRole('STAFF') && !hasRole('BUSINESS_MERCHANT') && !hasRole('PUDO_POINT')) {
      toast.error('Access denied');
      navigate('/dashboard');
    }
  }, [isAdmin, hasRole, navigate]);

  // Fetch items based on active tab
  const fetchItems = async () => {
    setLoading(true);
    try {
      let endpoint = '';
      switch (activeTab) {
        case 'product':
          endpoint = '/admin/products/';
          break;
        case 'merchant':
          endpoint = '/admin/businesses/';
          break;
        case 'pudo':
          endpoint = '/admin/businesses/?is_pudo=true';
          break;
        case 'driver':
          endpoint = '/admin/drivers/';
          break;
        case 'order':
          endpoint = '/orders/';
          break;
        case 'package':
          endpoint = '/packages/';
          break;
        default:
          endpoint = '/admin/products/';
      }
      
      const response = await api.get(endpoint);
      let data = response.data;
      
      // Handle different response formats
      if (data.products) data = data.products;
      if (data.items) data = data.items;
      if (Array.isArray(data)) {
        setItems(data);
      } else {
        setItems([]);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    setSelectedItems([]);
    setGeneratedCodes([]);
  }, [activeTab]);

  // Filter items by search
  const filteredItems = items.filter(item => {
    const searchLower = searchQuery.toLowerCase();
    const name = item.name || item.business_name || item.order_id || item.tracking_number || '';
    const id = item.id || '';
    const sku = item.sku || '';
    return name.toLowerCase().includes(searchLower) || 
           id.toLowerCase().includes(searchLower) ||
           sku.toLowerCase().includes(searchLower);
  });

  // Toggle item selection
  const toggleSelection = (item) => {
    const isSelected = selectedItems.some(s => s.id === item.id);
    if (isSelected) {
      setSelectedItems(selectedItems.filter(s => s.id !== item.id));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  // Select all visible items
  const selectAll = () => {
    setSelectedItems(filteredItems);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedItems([]);
  };

  // Generate QR codes for selected items
  const generateQRCodes = async () => {
    if (selectedItems.length === 0) {
      toast.error('Please select items to generate QR codes');
      return;
    }

    setGenerating(true);
    try {
      const requests = selectedItems.map(item => ({
        type: activeTab,
        id: item.id,
        label: item.sku || item.name || item.business_name || item.tracking_number || `${activeTab}-${item.id.slice(0, 8)}`,
        size: qrSize,
        include_label: includeLabel
      }));

      const response = await api.post('/qr/generate/bulk', { items: requests });
      setGeneratedCodes(response.data.codes);
      toast.success(`Generated ${response.data.count} QR codes`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate QR codes');
    } finally {
      setGenerating(false);
    }
  };

  // Print QR codes
  const handlePrint = () => {
    if (generatedCodes.length === 0) {
      toast.error('No QR codes to print');
      return;
    }
    window.print();
  };

  // Download all QR codes as a single image
  const downloadAll = async () => {
    if (generatedCodes.length === 0) {
      toast.error('No QR codes to download');
      return;
    }

    // Create a canvas to combine all QR codes
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const padding = 20;
    const codeWidth = qrSize + padding;
    const codeHeight = qrSize + padding;
    const cols = codesPerRow;
    const rows = Math.ceil(generatedCodes.length / cols);
    
    canvas.width = cols * codeWidth + padding;
    canvas.height = rows * codeHeight + padding;
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Load and draw each QR code
    for (let i = 0; i < generatedCodes.length; i++) {
      const code = generatedCodes[i];
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = padding + col * codeWidth;
      const y = padding + row * codeHeight;

      await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, x, y, qrSize, qrSize);
          resolve();
        };
        img.src = code.qr_data;
      });
    }

    // Download
    const link = document.createElement('a');
    link.download = `qr-codes-${activeTab}-${Date.now()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Downloaded QR codes');
  };

  // Get item display name
  const getItemName = (item) => {
    return item.name || item.business_name || item.tracking_number || `Item ${item.id?.slice(0, 8)}`;
  };

  const getItemSubtext = (item) => {
    if (item.sku) return `SKU: ${item.sku}`;
    if (item.email) return item.email;
    if (item.status) return `Status: ${item.status}`;
    return item.id?.slice(0, 12);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="qr-code-page">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-40 print:hidden">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="text-slate-400 hover:text-white transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
                <QrCode className="h-6 w-6 text-amber-400" />
                QR Code Printing
              </h1>
              <p className="text-slate-400 text-sm">
                Generate and print QR codes for tracking
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handlePrint}
              disabled={generatedCodes.length === 0}
              variant="outline"
              className="border-slate-700 text-slate-300"
              data-testid="print-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button
              onClick={downloadAll}
              disabled={generatedCodes.length === 0}
              className="bg-amber-500 text-slate-950 hover:bg-amber-400"
              data-testid="download-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
      </header>

      <main className="p-6 print:p-0">
        <div className="max-w-7xl mx-auto">
          {/* Type Tabs */}
          <div className="flex flex-wrap gap-2 mb-6 print:hidden">
            {QR_TYPES.map(type => {
              const Icon = type.icon;
              return (
                <button
                  key={type.id}
                  onClick={() => setActiveTab(type.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                    activeTab === type.id
                      ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                      : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                  }`}
                  data-testid={`tab-${type.id}`}
                >
                  <Icon className={`h-4 w-4 ${activeTab === type.id ? type.color : ''}`} />
                  {type.label}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Panel - Item Selection */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 print:hidden">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">
                  Select {QR_TYPES.find(t => t.id === activeTab)?.label}
                </h2>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={selectAll}
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-white"
                  >
                    Select All
                  </Button>
                  <Button
                    onClick={clearSelection}
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-white"
                  >
                    Clear
                  </Button>
                </div>
              </div>

              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, ID, or SKU..."
                  className="bg-slate-800 border-slate-700 pl-10 text-white"
                  data-testid="search-input"
                />
              </div>

              {/* Items List */}
              <div className="max-h-[400px] overflow-y-auto space-y-2">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-amber-400" />
                  </div>
                ) : filteredItems.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No items found
                  </div>
                ) : (
                  filteredItems.map(item => {
                    const isSelected = selectedItems.some(s => s.id === item.id);
                    return (
                      <button
                        key={item.id}
                        onClick={() => toggleSelection(item)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                          isSelected
                            ? 'bg-amber-500/20 border-amber-500/50'
                            : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                        }`}
                        data-testid={`item-${item.id}`}
                      >
                        <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                          isSelected ? 'bg-amber-500 border-amber-500' : 'border-slate-600'
                        }`}>
                          {isSelected && <CheckCircle className="h-4 w-4 text-slate-950" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white truncate">{getItemName(item)}</p>
                          <p className="text-slate-500 text-xs truncate">{getItemSubtext(item)}</p>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {/* Selection Summary & Generate */}
              <div className="mt-4 pt-4 border-t border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-400 text-sm">
                    {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} selected
                  </span>
                </div>
                
                {/* Options */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <Label className="text-slate-400 text-xs">Size (px)</Label>
                    <Input
                      type="number"
                      value={qrSize}
                      onChange={(e) => setQrSize(Math.min(500, Math.max(100, parseInt(e.target.value) || 200)))}
                      className="bg-slate-800 border-slate-700 text-white"
                      min={100}
                      max={500}
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Codes per Row</Label>
                    <Input
                      type="number"
                      value={codesPerRow}
                      onChange={(e) => setCodesPerRow(Math.min(6, Math.max(1, parseInt(e.target.value) || 3)))}
                      className="bg-slate-800 border-slate-700 text-white"
                      min={1}
                      max={6}
                    />
                  </div>
                </div>
                
                <label className="flex items-center gap-2 mb-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeLabel}
                    onChange={(e) => setIncludeLabel(e.target.checked)}
                    className="rounded border-slate-600"
                  />
                  <span className="text-slate-300 text-sm">Include label text</span>
                </label>

                <Button
                  onClick={generateQRCodes}
                  disabled={selectedItems.length === 0 || generating}
                  className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400"
                  data-testid="generate-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <QrCode className="h-4 w-4 mr-2" />
                      Generate QR Codes
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Right Panel - Generated QR Codes */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 print:bg-white print:border-0 print:p-0">
              <div className="flex items-center justify-between mb-4 print:hidden">
                <h2 className="text-lg font-semibold text-white">
                  Generated QR Codes ({generatedCodes.length})
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded ${viewMode === 'grid' ? 'bg-slate-700' : ''}`}
                  >
                    <Grid className="h-4 w-4 text-slate-400" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded ${viewMode === 'list' ? 'bg-slate-700' : ''}`}
                  >
                    <List className="h-4 w-4 text-slate-400" />
                  </button>
                </div>
              </div>

              {/* QR Codes Display */}
              <div 
                ref={printRef}
                className={`print:flex print:flex-wrap print:justify-start ${
                  viewMode === 'grid' 
                    ? `grid gap-4`
                    : 'space-y-4'
                }`}
                style={viewMode === 'grid' ? { gridTemplateColumns: `repeat(${codesPerRow}, 1fr)` } : {}}
              >
                {generatedCodes.length === 0 ? (
                  <div className="col-span-full text-center py-12 text-slate-500 print:hidden">
                    <QrCode className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p>Select items and generate QR codes</p>
                  </div>
                ) : (
                  generatedCodes.map((code, index) => (
                    <div
                      key={index}
                      className={`bg-white rounded-lg p-3 print:p-2 print:m-1 print:inline-block ${
                        viewMode === 'list' ? 'flex items-center gap-4' : 'text-center'
                      }`}
                      style={{ breakInside: 'avoid' }}
                    >
                      <img
                        src={code.qr_data}
                        alt={`QR Code for ${code.label}`}
                        className={viewMode === 'list' ? 'w-20 h-20' : 'w-full'}
                        style={{ maxWidth: qrSize, margin: viewMode === 'grid' ? '0 auto' : '0' }}
                      />
                      {viewMode === 'list' && (
                        <div className="text-slate-900">
                          <p className="font-medium">{code.label}</p>
                          <p className="text-sm text-slate-500">{code.type.toUpperCase()}</p>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #root, [data-testid="qr-code-page"], [data-testid="qr-code-page"] * {
            visibility: visible;
          }
          [data-testid="qr-code-page"] {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default QRCodePrinting;
